/* =====================================================================
   DRi-X-Ai — DriXWidget.jsx
   Floating launcher (bottom-right) + chat panel (compact + maximised).
   React port of the kilken_new DRi-X-Ai vanilla widget. Talks to the n8n
   "dashboard-bubble-ai" flow through the server-side proxy at
   /api/drix-proxy.php — the webhook URL, client id and any token stay
   server-side; the browser only ever sees this proxy.

   Conversation history is stored server-side (in an n8n data table, keyed to
   the signed-in dashboard user) and fetched through the proxy's "history"
   action — it is NOT kept in localStorage, so it follows the user across
   browsers and devices. Each assistant answer can be rated thumbs up/down
   (with an optional comment on a thumbs down) via the proxy's "feedback"
   action.

   Config injection (data-* attributes in the PHP original) becomes props +
   build-time env here; identity comes from the signed-in `user` prop but is
   re-derived and enforced server-side by the proxy.
   ===================================================================== */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import './DriXWidget.css';

const ENDPOINT = process.env.REACT_APP_DRIX_PROXY_BASE || '/api/drix-proxy.php';
const LOGO = process.env.REACT_APP_DRIX_LOGO_URL || '/drix-logo.png';
const NAME = 'DRi-X';
// Abort ceiling for a single request. Each request is short (the ack "ask" and
// the "poll" calls both return quickly), so this is just a safe upper bound.
const TIMEOUT_MS = 205 * 1000;

// Heavy queries run for minutes, so "ask" returns { status:"processing" } and
// we poll for the answer instead of holding a connection open.
const POLL_INTERVAL_MS = 3000;
const POLL_INTERVAL_SLOW_MS = 5000;
const POLL_BACKOFF_AFTER_MS = 30 * 1000;
const POLL_MAX_MS = 12 * 60 * 1000;
const WORKING_NOTES = [
  'Working on it',
  'Crunching the plant data',
  'Sifting through millions of readings',
  "Running the query — this one's a big one",
  'Aggregating the results',
  'Still working — thanks for your patience',
];

function sleep(ms) { return new Promise((r) => window.setTimeout(r, ms)); }
function fmtElapsed(ms) {
  let s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  s = s % 60;
  return m > 0 ? (m + 'm ' + (s < 10 ? '0' : '') + s + 's') : (s + 's');
}

/* ---- Markdown + chart rendering (ported verbatim; output is HTML-escaped
   first, so no raw HTML can pass through) ---------------------------------- */
const CHART_COLORS = ['#4cc9f0', '#7b2cbf', '#f72585', '#f97316', '#22c55e', '#60a5fa', '#eab308', '#ef4444'];

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function mdInline(s) {
  s = s.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*<\w])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');
  s = s.replace(/(^|\W)_([^_\n]+?)_(?!\w)/g, '$1<em>$2</em>');
  s = s.replace(/\[([^\]\n]+?)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+|\/[^\s)]*)\)/g,
    (_, t, u) => '<a href="' + u + '" target="_blank" rel="noopener noreferrer">' + t + '</a>');
  return s;
}
function mdTable(rows) {
  function cells(row) {
    return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
  }
  const head = cells(rows[0]);
  const body = rows.slice(2).map(cells);
  let html = '<div class="drix-md-tablewrap"><table class="drix-md-table"><thead><tr>';
  head.forEach((c) => { html += '<th>' + mdInline(c) + '</th>'; });
  html += '</tr></thead><tbody>';
  body.forEach((row) => {
    html += '<tr>';
    for (let c = 0; c < head.length; c++) { html += '<td>' + mdInline(row[c] != null ? row[c] : '') + '</td>'; }
    html += '</tr>';
  });
  return html + '</tbody></table></div>';
}
function num(v) { const n = parseFloat(v); return isFinite(n) ? n : 0; }
function fmtNum(v) {
  const a = Math.abs(v);
  if (a >= 1e9) { return (v / 1e9).toFixed(1) + 'B'; }
  if (a >= 1e6) { return (v / 1e6).toFixed(1) + 'M'; }
  if (a >= 1e3) { return (v / 1e3).toFixed(1) + 'k'; }
  return String(Math.round(v * 100) / 100);
}
function shortLabel(s) { s = String(s); return s.length > 11 ? s.slice(0, 10) + '…' : s; }
function chartLegend(names) {
  let h = '<div class="drix-chart-legend">';
  names.forEach((nm, i) => {
    h += '<span class="drix-chart-legend__item"><span class="drix-chart-legend__dot" style="background:' +
      CHART_COLORS[i % CHART_COLORS.length] + '"></span>' + escapeHtml(String(nm)) + '</span>';
  });
  return h + '</div>';
}
function renderChart(spec) {
  const type = String(spec.type || 'bar').toLowerCase();
  const labels = Array.isArray(spec.labels) ? spec.labels.map(String) : [];
  let series = Array.isArray(spec.series) ? spec.series.slice() : [];
  if (!series.length && Array.isArray(spec.data)) { series = [{ name: spec.name || '', data: spec.data }]; }
  series = series.filter((s) => s && Array.isArray(s.data));
  if (!series.length) { throw new Error('no series'); }
  const title = spec.title ? String(spec.title) : '';
  const W = 340, H = 215, padL = 40, padR = 12, padT = title ? 28 : 14, padB = 46;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  let svg = '<svg class="drix-chart" viewBox="0 0 ' + W + ' ' + H + '" role="img" preserveAspectRatio="xMidYMid meet">';
  if (title) { svg += '<text x="' + (W / 2) + '" y="16" text-anchor="middle" class="drix-chart__title">' + escapeHtml(title) + '</text>'; }

  if (type === 'pie' || type === 'doughnut') {
    const pdata = series[0].data.map(num);
    const total = pdata.reduce((a, b) => a + Math.max(0, b), 0) || 1;
    const cx = padL + plotW / 2, cy = padT + plotH / 2, r = Math.min(plotW, plotH) / 2 - 2;
    let ang = -Math.PI / 2;
    for (let p = 0; p < pdata.length; p++) {
      const frac = Math.max(0, pdata[p]) / total, a2 = ang + frac * Math.PI * 2;
      const x1 = cx + r * Math.cos(ang), y1 = cy + r * Math.sin(ang);
      const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
      svg += '<path d="M ' + cx.toFixed(1) + ' ' + cy.toFixed(1) + ' L ' + x1.toFixed(1) + ' ' + y1.toFixed(1) +
        ' A ' + r.toFixed(1) + ' ' + r.toFixed(1) + ' 0 ' + (frac > 0.5 ? 1 : 0) + ' 1 ' + x2.toFixed(1) + ' ' + y2.toFixed(1) +
        ' Z" fill="' + CHART_COLORS[p % CHART_COLORS.length] + '" opacity="0.9"/>';
      ang = a2;
    }
    svg += '</svg>';
    return '<div class="drix-chart-wrap">' + svg + chartLegend(labels.length ? labels : pdata.map((_, i) => '#' + (i + 1))) + '</div>';
  }

  const allVals = [];
  series.forEach((s) => { s.data.forEach((v) => { allVals.push(num(v)); }); });
  let maxV = Math.max.apply(null, allVals.concat([0]));
  const minV = Math.min.apply(null, allVals.concat([0]));
  if (maxV === minV) { maxV = minV + 1; }
  const n = labels.length || Math.max.apply(null, series.map((s) => s.data.length));
  const step = plotW / Math.max(1, n);
  function yOf(v) { return padT + plotH - ((num(v) - minV) / (maxV - minV)) * plotH; }

  for (let g = 0; g <= 2; g++) {
    const gv = minV + (g / 2) * (maxV - minV), gy = yOf(gv);
    svg += '<line x1="' + padL + '" y1="' + gy.toFixed(1) + '" x2="' + (W - padR) + '" y2="' + gy.toFixed(1) + '" class="drix-chart__grid"/>';
    svg += '<text x="' + (padL - 5) + '" y="' + (gy + 3).toFixed(1) + '" text-anchor="end" class="drix-chart__axis">' + fmtNum(gv) + '</text>';
  }
  for (let xi = 0; xi < n; xi++) {
    const lbl = labels[xi] != null ? labels[xi] : '';
    if (lbl !== '') {
      svg += '<text x="' + (padL + step * (xi + 0.5)).toFixed(1) + '" y="' + (H - 28) + '" text-anchor="middle" class="drix-chart__axis">' + escapeHtml(shortLabel(lbl)) + '</text>';
    }
  }

  if (type === 'line' || type === 'area') {
    series.forEach((s, si) => {
      const col = CHART_COLORS[si % CHART_COLORS.length];
      const pts = s.data.map((v, i) => (padL + step * (i + 0.5)).toFixed(1) + ',' + yOf(v).toFixed(1));
      if (type === 'area' && pts.length) {
        svg += '<polygon points="' + (padL + step * 0.5).toFixed(1) + ',' + yOf(minV).toFixed(1) + ' ' + pts.join(' ') + ' ' +
          (padL + step * (s.data.length - 0.5)).toFixed(1) + ',' + yOf(minV).toFixed(1) + '" fill="' + col + '" opacity="0.12"/>';
      }
      svg += '<polyline points="' + pts.join(' ') + '" fill="none" stroke="' + col + '" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>';
      s.data.forEach((v, i) => { svg += '<circle cx="' + (padL + step * (i + 0.5)).toFixed(1) + '" cy="' + yOf(v).toFixed(1) + '" r="2.5" fill="' + col + '"/>'; });
    });
  } else {
    const groupW = step * 0.7, bw = groupW / series.length, zeroY = yOf(minV < 0 ? 0 : minV);
    series.forEach((s, si) => {
      const col = CHART_COLORS[si % CHART_COLORS.length];
      s.data.forEach((v, i) => {
        const bx = padL + step * (i + 0.5) - groupW / 2 + bw * si;
        const vy = yOf(v), top = Math.min(vy, zeroY), hgt = Math.abs(zeroY - vy);
        svg += '<rect x="' + bx.toFixed(1) + '" y="' + top.toFixed(1) + '" width="' + (bw * 0.85).toFixed(1) + '" height="' + Math.max(0.5, hgt).toFixed(1) + '" rx="2" fill="' + col + '" opacity="0.9"/>';
      });
    });
  }
  svg += '<line x1="' + padL + '" y1="' + yOf(minV < 0 ? 0 : minV).toFixed(1) + '" x2="' + (W - padR) + '" y2="' + yOf(minV < 0 ? 0 : minV).toFixed(1) + '" class="drix-chart__axisline"/>';
  svg += '</svg>';
  const legend = series.length > 1 ? chartLegend(series.map((s, i) => s.name || ('Series ' + (i + 1)))) : '';
  return '<div class="drix-chart-wrap">' + svg + legend + '</div>';
}

function renderMarkdown(text) {
  let raw = String(text == null ? '' : text);
  const blocks = [];
  raw = raw.replace(/```([a-zA-Z0-9_-]*)\r?\n([\s\S]*?)```/g, (_, lang, code) => {
    blocks.push({ lang: (lang || '').toLowerCase(), code: code.replace(/\n+$/, '') });
    return '\nDRIXBLOCK' + (blocks.length - 1) + 'X\n';
  });
  const inline = [];
  raw = raw.replace(/`([^`\n]+?)`/g, (_, code) => { inline.push(code); return 'DRIXINLINE' + (inline.length - 1) + 'X'; });

  const lines = escapeHtml(raw).split('\n');
  function isSep(l) { return l.indexOf('|') !== -1 && /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l); }
  function isSpecial(t) {
    return /^DRIXBLOCK\d+X$/.test(t) || /^#{1,6}\s+/.test(t) || /^[-*+]\s+/.test(t) ||
      /^\d+[.)]\s+/.test(t) || /^>\s?/.test(t) || /^(-{3,}|\*{3,}|_{3,})$/.test(t);
  }
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const t = lines[i].trim();
    if (t === '') { i++; continue; }

    const bm = /^DRIXBLOCK(\d+)X$/.exec(t);
    if (bm) {
      const b = blocks[+bm[1]];
      if (b && (b.lang === 'chart' || b.lang === 'graph')) {
        try { out.push(renderChart(JSON.parse(b.code))); }
        catch (e) { out.push('<pre><code>' + escapeHtml(b.code) + '</code></pre>'); }
      } else {
        out.push('<pre><code>' + escapeHtml(b ? b.code : '') + '</code></pre>');
      }
      i++; continue;
    }
    if (t.indexOf('|') !== -1 && i + 1 < lines.length && isSep(lines[i + 1])) {
      const rows = [lines[i], lines[i + 1]]; i += 2;
      while (i < lines.length && lines[i].trim() !== '' && lines[i].indexOf('|') !== -1) { rows.push(lines[i]); i++; }
      out.push(mdTable(rows)); continue;
    }
    const hm = /^(#{1,6})\s+(.*)$/.exec(t);
    if (hm) { const lvl = Math.min(4, Math.max(3, hm[1].length)); out.push('<h' + lvl + '>' + mdInline(hm[2]) + '</h' + lvl + '>'); i++; continue; }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) { out.push('<hr>'); i++; continue; }
    if (/^>\s?/.test(t)) {
      const q = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { q.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      out.push('<blockquote>' + mdInline(q.join(' ')) + '</blockquote>'); continue;
    }
    if (/^[-*+]\s+/.test(t)) {
      const ul = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) { ul.push('<li>' + mdInline(lines[i].replace(/^\s*[-*+]\s+/, '')) + '</li>'); i++; }
      out.push('<ul>' + ul.join('') + '</ul>'); continue;
    }
    if (/^\d+[.)]\s+/.test(t)) {
      const ol = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) { ol.push('<li>' + mdInline(lines[i].replace(/^\s*\d+[.)]\s+/, '')) + '</li>'); i++; }
      out.push('<ol>' + ol.join('') + '</ol>'); continue;
    }
    const para = [];
    while (i < lines.length) {
      const pt = lines[i].trim();
      if (pt === '' || isSpecial(pt)) { break; }
      if (pt.indexOf('|') !== -1 && i + 1 < lines.length && isSep(lines[i + 1])) { break; }
      para.push(pt); i++;
    }
    if (para.length) { out.push('<p>' + mdInline(para.join('\n')).replace(/\n/g, '<br>') + '</p>'); }
  }
  return out.join('').replace(/DRIXINLINE(\d+)X/g, (_, idx) => '<code>' + escapeHtml(inline[+idx]) + '</code>');
}

/* ---- Conversation helpers --------------------------------------------- */
function newSessionId() {
  return 's' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
function stripUserPrefix(sid) {
  // The server namespaces session ids as "<userKey>:<token>", where userKey is
  // "u<id>" for DB users or "u0_<slug>" for env-fallback logins. Strip that
  // leading key to recover the bare per-conversation token — all rows returned
  // belong to the signed-in user and tokens never contain a colon, so the first
  // colon always separates the key from the token.
  return String(sid || '').replace(/^u[\w-]*:/, '');
}
function assistantHtml(m) {
  // Assistant content is immutable once set; render its markdown/charts once and
  // cache the HTML so it isn't re-parsed (incl. chart JSON.parse) on every bump.
  if (m._html == null) { m._html = renderMarkdown(m.content); }
  return m._html;
}
function makeTitle(t) {
  t = String(t || '').replace(/\s+/g, ' ').trim();
  return t.length > 60 ? t.slice(0, 60) + '…' : t;
}
function timeAgo(ms) {
  if (!ms) { return ''; }
  const diff = Math.max(0, Date.now() - ms);
  const s = Math.floor(diff / 1000);
  if (s < 60) { return 'just now'; }
  const mi = Math.floor(s / 60);
  if (mi < 60) { return mi + 'm ago'; }
  const h = Math.floor(mi / 60);
  if (h < 24) { return h + 'h ago'; }
  const d = Math.floor(h / 24);
  if (d < 30) { return d + 'd ago'; }
  const mo = Math.floor(d / 30);
  if (mo < 12) { return mo + 'mo ago'; }
  return Math.floor(mo / 12) + 'y ago';
}
function buildConversationsFromServer(rows) {
  const bySession = {};
  const order = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!r || !r.role) { continue; }
    let token = stripUserPrefix(r.session_id);
    if (!token) { token = 'default'; }
    if (!bySession[token]) {
      bySession[token] = { id: token, sessionId: token, title: '', messages: [], createdAt: 0, lastAt: 0 };
      order.push(token);
    }
    const conv = bySession[token];
    let ts = r.created_at ? Date.parse(r.created_at) : 0;
    if (!isFinite(ts)) { ts = 0; }
    if (ts && (!conv.createdAt || ts < conv.createdAt)) { conv.createdAt = ts; }
    if (ts && ts > conv.lastAt) { conv.lastAt = ts; }
    if (r.conversation_title && !conv.title) { conv.title = makeTitle(r.conversation_title); }
    conv.messages.push({
      role: r.role === 'assistant' ? 'assistant' : 'user',
      content: String(r.content == null ? '' : r.content),
      messageId: String(r.message_id == null ? '' : r.message_id),
      rating: String(r.rating == null ? '' : r.rating),
      ratingComment: String(r.rating_comment == null ? '' : r.rating_comment),
    });
  }
  const list = order.map((t) => bySession[t]);
  list.forEach((c) => {
    if (!c.title) {
      for (let j = 0; j < c.messages.length; j++) {
        if (c.messages[j].role === 'user') { c.title = makeTitle(c.messages[j].content); break; }
      }
    }
    if (!c.lastAt) { c.lastAt = c.createdAt; }
  });
  list.sort((a, b) => (b.lastAt || 0) - (a.lastAt || 0));
  return list;
}

/* ---- Backend calls ---------------------------------------------------- */
async function apiPost(payload) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'fetch' },
      credentials: 'include',
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    let text = '';
    try { text = await res.text(); } catch (e) { /* ignore */ }
    let data = null;
    try { data = JSON.parse(text); } catch (e) { /* keep null */ }
    if (!res.ok || (data && data.success === false)) {
      return { ok: false, error: (data && data.message) || ('Request failed (HTTP ' + res.status + ').'), data };
    }
    return { ok: true, data: data || {} };
  } catch (e) {
    if (e && e.name === 'AbortError') { return { ok: false, error: 'timeout' }; }
    return { ok: false, error: 'network' };
  } finally {
    window.clearTimeout(timer);
  }
}
async function postQuestion(question, sessionId, history) {
  const res = await apiPost({ action: 'ask', question, sessionId, history: history || [] });
  if (!res.ok) {
    const msg = res.error === 'timeout' ? (NAME + ' took too long to respond. Please try again.')
      : res.error === 'network' ? ('Could not reach ' + NAME + '. Check your connection and try again.')
        : res.error;
    return { ok: false, error: msg };
  }
  const data = res.data || {};
  if (data.status === 'processing') { return { ok: true, processing: true }; }
  const reply = typeof data.reply === 'string' ? data.reply : '';
  if (!reply) { return { ok: false, error: NAME + ' returned an empty response.' }; }
  return { ok: true, reply, messageId: typeof data.messageId === 'string' ? data.messageId : '' };
}
async function pollForAnswer(sessionId, lastAssistantId, baseStart, onTick) {
  const pollStart = Date.now();
  let tick = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (typeof onTick === 'function') {
      const note = WORKING_NOTES[Math.floor(tick / 3) % WORKING_NOTES.length];
      onTick(note + ' · ' + fmtElapsed(Date.now() - baseStart));
    }
    await sleep((Date.now() - pollStart) > POLL_BACKOFF_AFTER_MS ? POLL_INTERVAL_SLOW_MS : POLL_INTERVAL_MS);
    tick++;
    const res = await apiPost({ action: 'poll', sessionId, lastAssistantId: lastAssistantId || '' });
    if (res.ok && res.data && res.data.status === 'done') {
      return {
        ok: true,
        reply: typeof res.data.reply === 'string' ? res.data.reply : '',
        messageId: typeof res.data.messageId === 'string' ? res.data.messageId : '',
      };
    }
    if (Date.now() - pollStart > POLL_MAX_MS) { return { ok: true, timedOut: true }; }
  }
}
async function sendFeedback(messageId, rating, comment) {
  if (!messageId) { return { ok: false }; }
  return apiPost({ action: 'feedback', messageId, rating, comment: comment || '' });
}

const THUMB_UP_PATHS = ['M7 10v12', 'M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88z'];
const THUMB_DOWN_PATHS = ['M17 14V2', 'M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88z'];
function ThumbIcon({ paths }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

export default function DriXWidget({ user }) {
  const USERNAME = (user && (user.full_name || user.username)) || '';
  const USERLOGIN = (user && (user.email || user.username)) || '';

  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => (t + 1) % 1000000), []);

  const inputRef = useRef(null);
  const bodyRef = useRef(null);

  const stateRef = useRef(null);
  if (stateRef.current === null) {
    stateRef.current = {
      open: false,
      max: false,
      history: false,
      configured: true, // optimistic; refined by the config health check
      flash: false,
      conversations: [],
      activeConv: null,
      historyLoaded: false,
      inFlight: false,
      pending: null, // { status } while waiting for an answer
      notice: null,  // transient assistant notice (error / long-running)
      lastFocus: null,
      _flashTimer: null,
    };
  }
  const S = stateRef.current;

  const isConfigured = () => S.configured;

  /* ---- config health check + Escape key -------------------------------- */
  useEffect(() => {
    let alive = true;
    apiPost({ action: 'config' })
      .then((res) => {
        if (alive && res.ok && res.data && res.data.configured === false) {
          stateRef.current.configured = false;
          setTick((t) => (t + 1) % 1000000);
        }
      })
      .catch(() => {});
    const onKey = (e) => {
      // Reads/writes the stable state ref and setTick only — a mount-only
      // effect with a correct empty dependency array.
      const st = stateRef.current;
      if (e.key !== 'Escape' || !st.open) { return; }
      if (st.max) {
        st.max = false;
      } else {
        st.open = false; st.max = false; st.history = false;
        if (st.lastFocus && typeof st.lastFocus.focus === 'function') {
          try { st.lastFocus.focus(); } catch (err) { /* ignore */ }
        }
      }
      setTick((t) => (t + 1) % 1000000);
    };
    document.addEventListener('keydown', onKey);
    return () => { alive = false; document.removeEventListener('keydown', onKey); };
  }, []);

  /* ---- keep the transcript scrolled to the bottom on new content ------- */
  const scrollKey = [
    S.open,
    S.activeConv ? S.activeConv.id : '',
    S.activeConv ? S.activeConv.messages.length : 0,
    S.pending ? S.pending.status : '',
    S.notice ? 1 : 0,
  ].join('|');
  useEffect(() => {
    if (bodyRef.current) { bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }
  }, [scrollKey]);

  /* ---- helpers --------------------------------------------------------- */
  function autosize() {
    const el = inputRef.current;
    if (!el) { return; }
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }
  function flashUnconfigured() {
    S.flash = true; bump();
    if (S._flashTimer) { window.clearTimeout(S._flashTimer); }
    S._flashTimer = window.setTimeout(() => { S.flash = false; S._flashTimer = null; bump(); }, 650);
  }
  function newSession() { return newSessionId(); }
  function ensureConversation() {
    if (S.activeConv) { return; }
    const token = newSession();
    S.activeConv = { id: token, sessionId: token, title: '', messages: [], createdAt: Date.now(), lastAt: Date.now() };
    S.conversations.unshift(S.activeConv);
  }
  function indexOfConv(id) {
    for (let i = 0; i < S.conversations.length; i++) { if (S.conversations[i].id === id) { return i; } }
    return -1;
  }
  function touchActive() {
    if (!S.activeConv) { return; }
    const idx = indexOfConv(S.activeConv.id);
    if (idx > 0) {
      S.conversations.splice(idx, 1);
      S.conversations.unshift(S.activeConv);
    }
  }

  /* ---- history --------------------------------------------------------- */
  async function fetchHistory() {
    if (!isConfigured()) { bump(); return; }
    const res = await apiPost({ action: 'history' });
    if (!res.ok || !res.data || !Array.isArray(res.data.messages)) {
      S.historyLoaded = true; bump(); return;
    }
    const fresh = buildConversationsFromServer(res.data.messages);
    const keepActive = S.activeConv;
    S.conversations = fresh;
    if (keepActive) {
      const i = indexOfConv(keepActive.id);
      if (i !== -1) { S.activeConv = S.conversations[i]; }
      else if (keepActive.messages && keepActive.messages.length === 0) { S.conversations.unshift(keepActive); }
      else { S.activeConv = null; }
    }
    S.historyLoaded = true;
    bump();
  }

  /* ---- open / close ---------------------------------------------------- */
  function openPanel() {
    if (S.open) { return; }
    S.lastFocus = (typeof document !== 'undefined') ? document.activeElement : null;
    S.open = true; bump();
    window.setTimeout(() => { if (isConfigured() && inputRef.current) { try { inputRef.current.focus(); } catch (e) { /* ignore */ } } }, 30);
    fetchHistory();
  }
  function closePanel() {
    if (!S.open) { return; }
    S.open = false; S.max = false; S.history = false; bump();
    if (S.lastFocus && typeof S.lastFocus.focus === 'function') { try { S.lastFocus.focus(); } catch (e) { /* ignore */ } }
  }
  function toggleMax(forceState) {
    const goMax = (typeof forceState === 'boolean') ? forceState : !S.max;
    S.max = goMax;
    if (goMax) { S.history = true; }
    bump();
  }
  function toggleHistory() {
    const on = !S.history;
    S.history = on;
    if (on && !S.historyLoaded) { fetchHistory(); } else { bump(); }
  }

  /* ---- new / load conversation ---------------------------------------- */
  function newConversation() {
    if (S.inFlight) { return; }
    S.activeConv = null;
    S.pending = null;
    S.notice = null;
    bump();
    if (inputRef.current && isConfigured()) {
      inputRef.current.value = '';
      autosize();
      try { inputRef.current.focus(); } catch (e) { /* ignore */ }
    }
  }
  function loadConversation(id) {
    if (S.inFlight) { return; }
    const idx = indexOfConv(id);
    if (idx === -1) { return; }
    S.activeConv = S.conversations[idx];
    S.pending = null;
    S.notice = null;
    bump();
    if (inputRef.current && isConfigured()) { try { inputRef.current.focus(); } catch (e) { /* ignore */ } }
  }

  /* ---- send ------------------------------------------------------------ */
  async function send(text) {
    text = (text || '').trim();
    if (!text || S.inFlight) { return; }
    if (!isConfigured()) { flashUnconfigured(); return; }

    S.inFlight = true;
    S.notice = null;
    ensureConversation();

    const history = S.activeConv.messages.slice(-20).map((m) => ({ role: m.role, content: m.content }));
    S.activeConv.messages.push({ role: 'user', content: text });
    if (!S.activeConv.title) { S.activeConv.title = makeTitle(text); }
    S.activeConv.lastAt = Date.now();

    if (inputRef.current) { inputRef.current.value = ''; autosize(); }

    const sendStart = Date.now();
    S.pending = { status: '' };
    bump();

    let priorAssistantId = '';
    for (let pi = S.activeConv.messages.length - 1; pi >= 0; pi--) {
      if (S.activeConv.messages[pi].role === 'assistant' && S.activeConv.messages[pi].messageId) {
        priorAssistantId = S.activeConv.messages[pi].messageId;
        break;
      }
    }

    try {
      let result = await postQuestion(text, S.activeConv.sessionId, history);

      if (result && result.ok && result.processing) {
        S.pending.status = WORKING_NOTES[0] + ' · ' + fmtElapsed(Date.now() - sendStart);
        bump();
        result = await pollForAnswer(S.activeConv.sessionId, priorAssistantId, sendStart, (s) => {
          if (S.pending) { S.pending.status = s; bump(); }
        });
      }

      if (result && result.ok && result.reply) {
        S.activeConv.messages.push({ role: 'assistant', content: result.reply, messageId: result.messageId || '', rating: '', ratingComment: '' });
        S.activeConv.lastAt = Date.now();
        touchActive();
      } else if (result && result.ok && result.timedOut) {
        S.notice = "⏳ This one's taking longer than usual — it's a heavy query, so I'm still working on it in the background. Your answer will appear in this conversation shortly; you can reopen it from **Chat history** (the refresh button) in a moment.";
      } else {
        S.notice = (result && result.error) || ('Sorry — ' + NAME + ' could not answer that.');
      }
    } catch (err) {
      S.notice = 'Sorry — the request failed. ' + ((err && err.message) || '');
    } finally {
      S.pending = null;
      S.inFlight = false;
      bump();
      if (isConfigured() && inputRef.current) { try { inputRef.current.focus(); } catch (e) { /* ignore */ } }
    }
  }

  function onFormSubmit(e) {
    e.preventDefault();
    send(inputRef.current ? inputRef.current.value : '');
  }
  function onInputKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(inputRef.current ? inputRef.current.value : '');
    }
  }
  function onChipClick(text) {
    if (!isConfigured()) { flashUnconfigured(); return; }
    if (inputRef.current) { inputRef.current.value = text; autosize(); }
    send(text);
  }

  /* ---- feedback -------------------------------------------------------- */
  async function fbUp(msg) {
    msg.rating = 'up'; msg.ratingComment = ''; msg._fbCommentOpen = false; msg._fbStatus = 'Thanks for the feedback.';
    bump();
    const r = await sendFeedback(msg.messageId, 'up', '');
    if (!r.ok) { msg._fbStatus = 'Could not save feedback.'; bump(); }
  }
  async function fbDown(msg) {
    msg.rating = 'down'; msg._fbCommentOpen = true; msg._fbStatus = '';
    msg._fbComment = msg.ratingComment || '';
    bump();
    const r = await sendFeedback(msg.messageId, 'down', msg.ratingComment || '');
    if (!r.ok) { msg._fbStatus = 'Could not save feedback.'; bump(); }
  }
  async function fbSubmit(msg) {
    msg.ratingComment = (msg._fbComment || '').trim();
    msg._fbCommentOpen = false; msg._fbStatus = 'Thanks for the feedback.';
    bump();
    const r = await sendFeedback(msg.messageId, 'down', msg.ratingComment);
    if (!r.ok) { msg._fbStatus = 'Could not save feedback.'; bump(); }
  }
  function fbSkip(msg) {
    msg._fbCommentOpen = false; msg._fbStatus = 'Thanks for the feedback.';
    bump();
  }

  /* ---- render ---------------------------------------------------------- */
  const rootClass = 'drix-root' + (S.open ? ' is-open' : '') + (S.max ? ' is-max' : '') + (S.history ? ' is-history' : '');
  const configured = isConfigured();
  const msgs = S.activeConv ? S.activeConv.messages : [];
  const showWelcome = (!S.activeConv || msgs.length === 0) && !S.pending && !S.notice;

  return (
    <div className={rootClass}>
      <button
        type="button"
        className="drix-fab"
        onClick={openPanel}
        aria-label={'Open ' + NAME + ' assistant'}
        aria-expanded={S.open ? 'true' : 'false'}
      >
        <span className="drix-fab__inner">
          <img className="drix-fab__img" src={LOGO} alt={NAME} />
        </span>
      </button>

      <div
        className="drix-scrim"
        hidden={!S.open}
        onClick={() => { if (S.max) { toggleMax(false); } else { closePanel(); } }}
      />

      <section className="drix-panel" role="dialog" aria-label={NAME + ' assistant'} aria-modal="false" hidden={!S.open}>
        <header className="drix-panel__head">
          <span className="drix-panel__brand">
            <span className="drix-avatar"><img src={LOGO} alt="" /></span>
            <span className="drix-panel__title">
              <span className="drix-panel__name">{NAME}</span>
              <span className="drix-panel__status">
                <span className={'drix-dot' + (configured ? ' is-on' : '')} aria-hidden="true" />
                <span className="drix-status-text">{configured ? 'Online · your data copilot' : 'Not configured'}</span>
              </span>
            </span>
          </span>
          <span className="drix-panel__actions">
            <button type="button" className="drix-iconbtn" onClick={toggleHistory} aria-label="Chat history" title="Chat history" aria-pressed={S.history ? 'true' : 'false'}>
              <svg className="drix-iconbtn__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 11.5a8.4 8.4 0 0 1-8.9 8.4 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.3A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />
              </svg>
            </button>
            <button type="button" className="drix-iconbtn" onClick={newConversation} aria-label="New conversation" title="Start a new conversation">
              <svg className="drix-iconbtn__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M23 4v6h-6" /><path d="M20.5 15a9 9 0 1 1-2.1-9.4L23 10" />
              </svg>
            </button>
            {!S.max && (
              <button type="button" className="drix-iconbtn" onClick={() => toggleMax(true)} aria-label="Maximise">
                <svg className="drix-iconbtn__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M16 21h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              </button>
            )}
            {S.max && (
              <button type="button" className="drix-iconbtn drix-iconbtn--minimize" onClick={() => toggleMax(false)} aria-label="Restore">
                <svg className="drix-iconbtn__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M16 21v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              </button>
            )}
            <button type="button" className="drix-iconbtn" onClick={closePanel} aria-label="Close">
              <svg className="drix-iconbtn__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        </header>

        <div className="drix-main">
          <aside className="drix-history" aria-label="Conversation history">
            <div className="drix-history__head">
              <span className="drix-history__title">Chat history</span>
              <button type="button" className="drix-iconbtn drix-iconbtn--sm" onClick={fetchHistory} aria-label="Refresh history" title="Refresh">
                <svg className="drix-iconbtn__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M23 4v6h-6" /><path d="M20.5 15a9 9 0 1 1-2.1-9.4L23 10" />
                </svg>
              </button>
            </div>
            <div className="drix-history__list">
              {S.conversations.length === 0 ? (
                <div className="drix-history__empty">
                  <p className="drix-history__empty-text">No conversations yet. Start by asking {NAME} a question.</p>
                </div>
              ) : (
                <ol className="drix-history__items">
                  {S.conversations.map((c) => {
                    const isActive = S.activeConv && c.id === S.activeConv.id;
                    const title = c.title && c.title.trim() ? c.title : 'New conversation';
                    const when = timeAgo(c.lastAt || c.createdAt);
                    return (
                      <li key={c.id} className={'drix-history__item' + (isActive ? ' is-active' : '')}>
                        <button type="button" className="drix-history__select" onClick={() => loadConversation(c.id)} title={title}>
                          <span className="drix-history__item-title">{title}</span>
                          {when ? <span className="drix-history__item-time">{when}</span> : null}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </aside>

          <div className="drix-body" ref={bodyRef}>
            {showWelcome && (
              <div className="drix-welcome">
                <div className="drix-logo"><img className="drix-logo__img" src={LOGO} alt={NAME} /></div>
                <p className="drix-tagline">Ask me about your plant data, readings, reagents or production — I’ll find the answer for you.</p>
                {USERNAME ? <p className="drix-signedin" title={USERLOGIN && USERLOGIN !== USERNAME ? USERLOGIN : undefined}>Signed in as {USERNAME}</p> : null}
                <p className="drix-chips__label">TRY ASKING</p>
                <div className="drix-chips">
                  <button type="button" className="drix-chip" onClick={() => onChipClick("Summarise today’s plant status")}>Summarise today’s plant status</button>
                  <button type="button" className="drix-chip" onClick={() => onChipClick('Which readings look out of range?')}>Which readings look out of range?</button>
                  <button type="button" className="drix-chip" onClick={() => onChipClick('Show reagent levels this week')}>Show reagent levels this week</button>
                  <button type="button" className="drix-chip" onClick={() => onChipClick('Explain the latest production figures')}>Explain the latest production figures</button>
                </div>
              </div>
            )}

            <ol className="drix-messages" aria-live="polite">
              {msgs.map((m, idx) => (
                m.role === 'user' ? (
                  <li key={idx} className="drix-msg drix-msg--user">
                    <div className="drix-msg__bubble">
                      <span className="drix-msg__role">You</span>
                      <div className="drix-msg__text">{m.content}</div>
                    </div>
                  </li>
                ) : (
                  <li key={idx} className="drix-msg drix-msg--ai">
                    <div className="drix-msg__bubble">
                      <span className="drix-msg__role">{NAME}</span>
                      <div className="drix-msg__text" dangerouslySetInnerHTML={{ __html: assistantHtml(m) }} />
                      {m.messageId ? (
                        <div className="drix-fb">
                          <button type="button" className={'drix-fb__btn drix-fb__btn--up' + (m.rating === 'up' ? ' is-active' : '')} onClick={() => fbUp(m)} aria-label="Good response" title="Good response">
                            <ThumbIcon paths={THUMB_UP_PATHS} />
                          </button>
                          <button type="button" className={'drix-fb__btn drix-fb__btn--down' + (m.rating === 'down' ? ' is-active' : '')} onClick={() => fbDown(m)} aria-label="Bad response" title="Bad response">
                            <ThumbIcon paths={THUMB_DOWN_PATHS} />
                          </button>
                          <span className="drix-fb__status" aria-live="polite">{m._fbStatus || ''}</span>
                          {m._fbCommentOpen && (
                            <div className="drix-fb__comment">
                              <textarea
                                className="drix-fb__input"
                                rows={2}
                                maxLength={2000}
                                placeholder="Optional: what was wrong with this answer?"
                                value={m._fbComment || ''}
                                onChange={(e) => { m._fbComment = e.target.value; bump(); }}
                                autoFocus
                              />
                              <div className="drix-fb__actions">
                                <button type="button" className="drix-fb__submit" onClick={() => fbSubmit(m)}>Send feedback</button>
                                <button type="button" className="drix-fb__skip" onClick={() => fbSkip(m)}>Skip</button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </li>
                )
              ))}

              {S.pending && (
                <li className="drix-msg drix-msg--ai drix-msg--typing">
                  <div className="drix-msg__bubble">
                    <span className="drix-msg__role">{NAME}</span>
                    <div className="drix-thinking">
                      <span className="drix-typing" aria-label={NAME + ' is thinking'}><span /><span /><span /></span>
                      <span className="drix-thinking__text">{S.pending.status || 'Thinking…'}</span>
                    </div>
                  </div>
                </li>
              )}

              {!S.pending && S.notice && (
                <li className="drix-msg drix-msg--ai">
                  <div className="drix-msg__bubble">
                    <span className="drix-msg__role">{NAME}</span>
                    <div className="drix-msg__text" dangerouslySetInnerHTML={{ __html: renderMarkdown(S.notice) }} />
                  </div>
                </li>
              )}
            </ol>
          </div>
        </div>

        <footer className="drix-composer">
          {!configured && (
            <div className={'drix-unconfigured' + (S.flash ? ' is-flash' : '')}>
              <span className="drix-unconfigured__text">
                {NAME} is not configured yet. Add <code>DRIX_WEBHOOK_URL</code> and <code>DRIX_CLIENT_ID</code> to the <code>.env</code> file.
              </span>
            </div>
          )}
          <form className="drix-input" onSubmit={onFormSubmit}>
            <textarea
              className="drix-textarea"
              ref={inputRef}
              rows={1}
              maxLength={4000}
              placeholder={'Ask ' + NAME + ' anything…'}
              disabled={!configured}
              onInput={autosize}
              onKeyDown={onInputKeyDown}
            />
            <button type="submit" className={'drix-send' + (S.inFlight ? ' is-busy' : '')} aria-label="Send" disabled={!configured || S.inFlight}>
              <svg className="drix-send__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </form>
          <p className="drix-footer">{NAME} can make mistakes · Press <kbd>Enter</kbd> to send, <kbd>Shift</kbd>+<kbd>Enter</kbd> for a new line</p>
        </footer>
      </section>
    </div>
  );
}
